/**
 * API endpoints for matches
 */
export class MatchesAPI {
    apiClient;
    controllerUrl;
    GAME_OVERRIDE;
    /**
     * Create a new MatchesAPI
     * @param apiClient - The API client
     * @param baseUrl - The base URL for API requests
     * @param gameOverride - Game override constants
     */
    constructor(apiClient, baseUrl, gameOverride) {
        this.apiClient = apiClient;
        this.controllerUrl = `${baseUrl}/Matches`;
        this.GAME_OVERRIDE = gameOverride;
    }
    /**
     * Get match info
     * @param matchId - The match ID
     * @returns Promise with the match
     */
    async getMatch(matchId) {
        if (!matchId)
            return null;
        const url = `/${matchId}`;
        return this.apiClient.get(this.controllerUrl + url);
    }
    /**
     * Get match moves
     * @param matchId - The match ID
     * @returns Promise with the moves
     */
    async getMatchMoves(matchId) {
        if (!matchId)
            return null;
        const url = `/${matchId}/moves`;
        return this.apiClient.get(this.controllerUrl + url);
    }
    /**
     * Create match
     * @param match - The match data
     * @returns Promise with the created match
     */
    async postMatch(match) {
        console.log("postMatch", match.match_id);
        if (!match || !match.match_id)
            return null;
        return this.apiClient.post(this.controllerUrl, match);
    }
    /**
     * Save move
     * @param matchId - The match ID
     * @param move - The move data
     * @returns Promise with the result
     */
    async postMatchMove(matchId, move) {
        if (!matchId)
            return null;
        if (!move)
            return null;
        const url = `/${matchId}/moves`;
        return this.apiClient.post(this.controllerUrl + url, move);
    }
    /**
     * Finish match
     * @param matchId - The match ID
     * @param gameOverride - Game override
     * @param whiteScore - White score
     * @param blackScore - Black score
     * @param result - Result
     * @param moves - Number of moves
     * @returns Promise with the result
     */
    async putMatch(matchId, gameOverride, whiteScore, blackScore, result, moves) {
        console.log("putMatch", matchId, gameOverride, whiteScore, blackScore, result, moves);
        if (!matchId)
            return null;
        const url = `/${matchId}`;
        return this.apiClient.put(this.controllerUrl + url, {
            player_1_score: whiteScore * 2,
            player_2_score: blackScore * 2,
            result: result,
            moves: moves,
            leaderboard: true,
            rating: gameOverride === this.GAME_OVERRIDE.PVAI ||
                gameOverride === this.GAME_OVERRIDE.SCENARIO
                ? false
                : whiteScore + blackScore > 0,
        });
    }
    /**
     * Admin update match
     * @param userId - The user ID
     * @param matchId - The match ID
     * @param body - The update data
     * @returns Promise with the result
     */
    async adminPutMatch(userId, matchId, body) {
        console.log("adminPutMatch", matchId);
        if (!matchId)
            return null;
        const url = `/${userId}/modify/${matchId}`;
        return this.apiClient.put(this.controllerUrl + url, {
            result: body.result,
            player_1_score: body.whiteScore * 2,
            player_2_score: body.blackScore * 2,
            duration: body.duration,
            started: body.startDate,
            ended: body.endDate,
            reverse_rating: body.reverseRating,
            rating: body.updateRating,
        });
    }
    /**
     * Get multiple player rating & mastery levels
     * @param usernames - The usernames
     * @returns Promise with the stats
     */
    async getPlayersStats(usernames) {
        if (!usernames || !usernames.length)
            return null;
        const url = "/players_stats";
        return this.apiClient.get(this.controllerUrl + url, {
            PlayerId: usernames.join(","),
        });
    }
    /**
     * Get 1 player mastery level
     * @param username - The username
     * @returns Promise with the stats
     */
    async getPlayerStats(username) {
        if (!username)
            return null;
        const url = `/${username}/stats`;
        return this.apiClient.get(this.controllerUrl + url);
    }
    /**
     * Get 1 player rating
     * @param username - The username
     * @returns Promise with the rating
     */
    async getPlayerRating(username) {
        if (!username)
            return null;
        const url = `/${username}/rating`;
        return this.apiClient.get(this.controllerUrl + url);
    }
    /**
     * Get 1 player match history
     * @param username - The username
     * @param params - Query parameters
     * @returns Promise with the matches
     */
    async getPlayerMatches(username, params = {}) {
        if (!username)
            return null;
        const url = `/${username}/matches`;
        return this.apiClient.getPage(this.controllerUrl + url, params);
    }
}
